import { getAccessToken, getUser, refresh as authRefresh, getApiUrl } from './auth-client.js';

function apiUrl(){ return getApiUrl(); }

function sleep(ms){ return new Promise(resolve => setTimeout(resolve, ms)); }

function parseEventBlock(block){
  const lines = String(block || '').split(/\r?\n/);
  let event = 'message'; let id = ''; const data = [];
  for(const line of lines){
    if(line.startsWith('event:')) event = line.slice(6).trim();
    else if(line.startsWith('id:')) id = line.slice(3).trim();
    else if(line.startsWith('data:')) data.push(line.slice(5).trimStart());
  }
  if(!data.length) return null;
  let payload = data.join('\n');
  try { payload = JSON.parse(payload); } catch(_) {}
  return { event, id, data: payload };
}

export function createCaseRealtimeClient({ onEvent = () => {}, onState = () => {} } = {}){
  let caseId = '';
  let stopped = false;
  let controller = null;
  let loopId = 0;
  let retry = 0;
  let connected = false;
  let heartbeatTimer = null;
  let presenceActivity = 'viewing';
  let lastError = '';

  function publish(extra = {}){
    const value = { connected, caseId: caseId || null, retry, lastError, ...extra };
    try { onState(value); } catch(_) {}
    try { window.dispatchEvent(new CustomEvent('mindplace:realtime-state', { detail:value })); } catch(_) {}
    return value;
  }

  async function heartbeat(){
    if(stopped || !caseId || !getAccessToken()) return;
    try{
      const token = getAccessToken();
      const response = await fetch(`${apiUrl()}/collaboration/cases/${encodeURIComponent(caseId)}/presence`, { method:'POST', credentials:'include', headers:{'Content-Type':'application/json', Authorization:`Bearer ${token}`}, body:JSON.stringify({ activity:presenceActivity, displayName:getUser()?.name || getUser()?.email || 'Usuario' }) });
      if(response.status === 401 && getAccessToken()){
        const refreshed = await authRefresh(false);
        if(refreshed) return heartbeat();
      }
      if(!response.ok) throw new Error(`HTTP ${response.status}`);
      lastError = '';
      publish({ lastHeartbeatAt:Date.now() });
    }catch(error){
      lastError = error?.message || 'No se pudo actualizar la presencia.';
      publish({});
    }
  }

  function startHeartbeat(){
    if(heartbeatTimer) clearInterval(heartbeatTimer);
    void heartbeat();
    heartbeatTimer = setInterval(() => void heartbeat(), 10000);
  }

  async function stopPresence(id){
    if(!id || !getAccessToken()) return;
    try{
      await fetch(`${apiUrl()}/collaboration/cases/${encodeURIComponent(id)}/presence`, { method:'DELETE', credentials:'include', headers:{Authorization:`Bearer ${getAccessToken()}`} });
    }catch(_){}
  }

  async function loop(id, myLoop){
    try{
      controller = new AbortController();
      const token = getAccessToken();
      if(!token || stopped || id !== caseId || myLoop !== loopId) return;
      const response = await fetch(`${apiUrl()}/realtime/cases/${encodeURIComponent(id)}/stream`, { credentials:'include', headers:{Accept:'text/event-stream', Authorization:`Bearer ${token}`}, signal:controller.signal, cache:'no-store' });
      if(response.status === 401){
        const refreshed = await authRefresh(false);
        if(refreshed && myLoop === loopId){ retry = 0; return loop(id,myLoop); }
        throw new Error('Sesión no válida para la colaboración en tiempo real.');
      }
      if(!response.ok) throw new Error(`Realtime HTTP ${response.status}`);
      if(!response.body) throw new Error('El navegador no soporta streaming de colaboración.');
      connected = true; retry = 0; lastError = ''; publish({ connected:true });
      const reader = response.body.getReader();
      const decoder = new TextDecoder(); let buffer = '';
      while(!stopped && id === caseId && myLoop === loopId){
        const {done,value} = await reader.read();
        if(done) break;
        buffer += decoder.decode(value,{stream:true});
        let idx;
        while((idx = buffer.indexOf('\n\n')) >= 0){
          const block = buffer.slice(0,idx); buffer = buffer.slice(idx+2);
          const parsed = parseEventBlock(block); if(!parsed) continue;
          if(parsed.event === 'ping') continue;
          try { onEvent({ ...parsed.data, event:parsed.event, sseId:parsed.id }); } catch(_) {}
        }
      }
      try { reader.releaseLock(); } catch(_) {}
      if(stopped || id !== caseId || myLoop !== loopId) return;
      connected = false; publish({ connected:false, disconnectedAt:Date.now() });
    }catch(error){
      if(stopped || id !== caseId || myLoop !== loopId) return;
      connected = false;
      if(error?.name === 'AbortError') return;
      lastError = error?.message || 'Conexión en tiempo real perdida.';
      publish({ connected:false });
    }finally{
      if(stopped || id !== caseId || myLoop !== loopId) return;
      retry = Math.min(retry + 1, 6);
      const delay = Math.min(1000 * 2 ** (retry - 1), 10000);
      publish({ reconnectInMs:delay });
      await sleep(delay);
      if(!stopped && id === caseId && myLoop === loopId) void loop(id,myLoop);
    }
  }

  function start(id){
    const next = String(id || '').trim();
    if(next === caseId && controller && !controller.signal.aborted) return;
    const previous = caseId;
    stop(false);
    caseId = next;
    retry = 0; lastError = '';
    if(!caseId || !getAccessToken()){ publish({ connected:false }); return; }
    startHeartbeat();
    const myLoop = ++loopId;
    void loop(caseId,myLoop);
    if(previous && previous !== caseId) void stopPresence(previous);
  }

  function stop(clearCase = true){
    if(controller){ try { controller.abort(); } catch(_) {} controller = null; }
    if(heartbeatTimer){ clearInterval(heartbeatTimer); heartbeatTimer = null; }
    connected = false;
    const previous = caseId;
    if(clearCase) caseId = '';
    loopId += 1;
    if(previous && getAccessToken()) void stopPresence(previous);
    publish({ connected:false, caseId:caseId || null });
  }

  function setActivity(activity){ presenceActivity = String(activity || 'viewing').slice(0,40) || 'viewing'; void heartbeat(); }
  function getState(){ return { connected, caseId:caseId || null, retry, lastError }; }
  function destroy(){ stopped = true; stop(true); }

  return { start, stop, setActivity, getState, destroy };
}
